import Phaser from 'phaser';
import MenuScene from './scenes/MenuScene';
import GameScene from './scenes/GameScene';
import { PokerClient } from './network/PokerClient';
import { audiusPlayerOverlay } from './audio/AudiusPlayerOverlay';
import { injectSpeedInsights } from '@vercel/speed-insights';
import { inject as injectAnalytics } from '@vercel/analytics';

const config: Phaser.Types.Core.GameConfig = {
  type: Phaser.AUTO,
  width: 1920,
  height: 1080,
  parent: 'game-container',
  backgroundColor: '#000000',
  scale: {
    mode: Phaser.Scale.FIT,
    autoCenter: Phaser.Scale.CENTER_BOTH,
  },
  dom: {
    createContainer: true,
  },
  scene: [MenuScene, GameScene],
};

// Global poker client instance
export const pokerClient = new PokerClient();

const game = new Phaser.Game(config);
audiusPlayerOverlay.mount();
injectSpeedInsights();
injectAnalytics();

// Prevent accidental page refresh/close while the user is at a table.
// Notes:
// - Browsers intentionally do not allow custom text in the native beforeunload dialog.
// - For keyboard refresh shortcuts (F5/Ctrl+R/Cmd+R) we can show our own explicit message.
let allowPageUnload = false;
const shouldWarnBeforeUnload = (): boolean => {
  if (allowPageUnload) return false;
  // If a tableId is set, user is currently seated/connected to a table.
  // This is the safest signal available globally.
  return Boolean(pokerClient.tableId);
};

const beforeUnloadHandler = (event: BeforeUnloadEvent): void => {
  if (!shouldWarnBeforeUnload()) return;
  event.preventDefault();
  // Required for Chrome/Firefox to trigger the confirmation dialog.
  event.returnValue = '';
};

const isKeyboardRefreshEvent = (event: KeyboardEvent): boolean => {
  if (event.key === 'F5') return true;
  if ((event.ctrlKey || event.metaKey) && (event.key === 'r' || event.key === 'R')) return true;
  return false;
};

const keydownHandler = (event: KeyboardEvent): void => {
  if (!isKeyboardRefreshEvent(event)) return;
  if (!shouldWarnBeforeUnload()) return;

  event.preventDefault();
  event.stopPropagation();

  const message =
    'Odświeżenie strony spowoduje wyjście do menu i odejście od stołu. Czy na pewno chcesz kontynuować?';
  const confirmed = window.confirm(message);
  if (!confirmed) return;

  // Best-effort: request leaving the table before reloading.
  // Do not block reload on network; browsers may ignore long tasks here.
  allowPageUnload = true;
  try {
    void pokerClient.leaveTable();
  } catch {
    // ignore
  }

  // Remove handler to avoid redundant prompts during the confirmed reload.
  window.removeEventListener('beforeunload', beforeUnloadHandler);
  window.location.reload();
};

window.addEventListener('beforeunload', beforeUnloadHandler);
window.addEventListener('keydown', keydownHandler, { capture: true });

// Expose for debugging
(window as any).game = game;
(window as any).pokerClient = pokerClient;
